import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ReportTabsProvider } from '@/contexts/ReportTabsContext';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { GlobalShortcutsProvider } from '@/hooks/useGlobalShortcut';
import { KeyboardShortcutsDialog } from '@/components/KeyboardShortcutsDialog';
import { CommandPalette } from '@/components/CommandPalette';
import LoginPage from '@/pages/LoginPage';
import LandingPage from '@/pages/LandingPage';
import AuthCallback from '@/pages/AuthCallback';
import Layout from '@/components/Layout';
import ForcePasswordChange from '@/components/ForcePasswordChange';
import DashboardPage from '@/pages/DashboardPage';
import DashboardBuilderPage from '@/pages/DashboardBuilderPage';
import ReportsPage from '@/pages/ReportsPage';
import ReportBuilderPage from '@/pages/ReportBuilderPage';
import ReportTabsPage from '@/pages/ReportTabsPage';
import ReportViewPage from '@/pages/ReportViewPage';
import DataSourcesPage from '@/pages/DataSourcesPage';
import AICopilotPage from '@/pages/ExplorePage';
import AiCoveragePage from '@/pages/AiCoveragePage';
import BillingPage from '@/pages/BillingPage';
import SemanticModelsPage from '@/pages/SemanticModelsPage';
import AggregateGovernancePage from '@/pages/AggregateGovernancePage';
import ModelComparePage from '@/pages/ModelComparePage';
import ReportComparePage from '@/pages/ReportComparePage';
import ChangeListsPage from '@/pages/ChangeListsPage';
import AIUsagePage from '@/pages/AIUsagePage';
import AccessControlPage from '@/pages/AccessControlPage';
import PiiCompliancePage from '@/pages/PiiCompliancePage';
import GlossaryPage from '@/pages/GlossaryPage';
import MessagesPage from '@/pages/MessagesPage';
import ActivityPage from '@/pages/ActivityPage';
import DiscussionsPage from '@/pages/DiscussionsPage';
import SchedulingPage from '@/pages/SchedulingPage';
import AlertsPage from '@/pages/AlertsPage';
import AdminPage from '@/pages/AdminPage';
import AdminJobsPage from '@/pages/AdminJobsPage';
import InboundEmailsPage from '@/pages/InboundEmailsPage';
import ShareLeadsPage from '@/pages/ShareLeadsPage';
import SignupInvitesPage from '@/pages/SignupInvitesPage';
import SignupRequestsPage from '@/pages/SignupRequestsPage';
import RegisterPage from '@/pages/RegisterPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DataPrepPage from '@/pages/DataPrepPage';
import SettingsPage from '@/pages/SettingsPage';
import EmbedPage from '@/pages/EmbedPage';
import InvestigationSnapshotPage from '@/pages/InvestigationSnapshotPage';
import VisualLibraryPage from '@/pages/VisualLibraryPage';
import VisualEditorPage from '@/pages/VisualEditorPage';
import HelpPage from '@/pages/HelpPage';
import TenantAdminPage from '@/pages/TenantAdminPage';
import KpiMonitorPage from '@/pages/KpiMonitorPage';
import LoggingPage from '@/pages/LoggingPage';
import LineagePage from '@/pages/LineagePage';
import EmbedManagerPage from '@/pages/EmbedManagerPage';
import AnomalyScanPage from '@/pages/AnomalyScanPage';
import ForecastSentinelsPage from '@/pages/ForecastSentinelsPage';
import QuickstartPage from '@/pages/QuickstartPage';
import DeployStatusPage from '@/pages/DeployStatusPage';
import StatusPage from '@/pages/StatusPage';
import ExploreEvalPage from '@/pages/ExploreEvalPage';
import AllSchedulesPage from '@/pages/AllSchedulesPage';
import TenantSchedulesPage from '@/pages/TenantSchedulesPage';
import AdminTenantsPage from '@/pages/AdminTenantsPage';
import RevenueDashboardPage from '@/pages/RevenueDashboardPage';
import InsightsFeedPage from '@/pages/InsightsFeedPage';
import LLMSettingsPage from '@/pages/LLMSettingsPage';
import StorageSettingsPage from '@/pages/StorageSettingsPage';
import SystemUpdatesPage from '@/pages/SystemUpdatesPage';
import GitBackupPage from '@/pages/GitBackupPage';
import VoiceSettingsPage from '@/pages/VoiceSettingsPage';
import AiAgentsPage from '@/pages/AiAgentsPage';
import AiTraceLogPage from '@/pages/AiTraceLogPage';
import InvestigationsHistoryPage from '@/pages/InvestigationsHistoryPage';
import ExecutiveSummariesPage from '@/pages/ExecutiveSummariesPage';
import MorningBriefPage from '@/pages/MorningBriefPage';
import InvestigationAnalyticsPage from '@/pages/InvestigationAnalyticsPage';
import InvestigationDiffPage from '@/pages/InvestigationDiffPage';
import CertificationQueuePage from '@/pages/CertificationQueuePage';
import DevelopersPage from '@/pages/DevelopersPage';
import PlatformAdminPage from '@/pages/PlatformAdminPage';
import EmbedInsightsPage from '@/pages/EmbedInsightsPage';
import EmbedKpiMonitorPage from '@/pages/EmbedKpiMonitorPage';
import EmbedContentViewerPage from '@/pages/EmbedContentViewerPage';
import EmbedActivityFeedPage from '@/pages/EmbedActivityFeedPage';
import EmbedBootstrapPage from '@/pages/EmbedBootstrapPage';
import PublicDocsPage from '@/pages/PublicDocsPage';
import EmbedPlaygroundPage from '@/pages/EmbedPlaygroundPage';
import EmbedExplorePage from '@/pages/EmbedExplorePage';
import EmbedInvestigatePage from '@/pages/EmbedInvestigatePage';
import PublicInvestigationPage from '@/pages/PublicInvestigationPage';
import ConferenceDemoPage from '@/pages/ConferenceDemoPage';
import EmbedMarketplacePage from '@/pages/EmbedMarketplacePage';
import EmbedErDiagramPage from '@/pages/EmbedErDiagramPage';
import EmbedAdminPanelPage from '@/pages/EmbedAdminPanelPage';
import { EmbedTokenProvider } from '@/lib/embedTokenBridge';
import { CollabProvider } from '@/contexts/CollabContext';
import VersionControlPage from '@/pages/VersionControlPage';
import PlatformSiteRootPage from '@/pages/PlatformSiteRootPage';
import FeatureFlagsPage from '@/pages/FeatureFlagsPage';
import PlatformBillingRollupPage from '@/pages/PlatformBillingRollupPage';
import AdministrationHubPage from '@/pages/AdministrationHubPage';
import MetricRegistryPage from '@/pages/MetricRegistryPage';
import DataCatalogPage from '@/pages/DataCatalogPage';
import CalculationPacksPage from '@/pages/CalculationPacksPage';
import DataQualityPage from '@/pages/DataQualityPage';
import ExploreKpisPage from '@/pages/ExploreKpisPage';
import PredictiveModelsPage from '@/pages/PredictiveModelsPage';
import RiskSignalsPage from '@/pages/RiskSignalsPage';
import DriftAlertCenterPage from '@/pages/DriftAlertCenterPage';
import PackLibraryPage from '@/pages/PackLibraryPage';
import RecommendationsPage from '@/pages/RecommendationsPage';
import RecommendationsHubPage from '@/pages/RecommendationsHubPage';
import MyWorkPage from '@/pages/MyWorkPage';
import RecommendationDetailPage from '@/pages/RecommendationDetailPage';
import DecisionPoliciesPage from '@/pages/DecisionPoliciesPage';
import NotificationsPage from '@/pages/NotificationsPage';
import ResultsPage from '@/pages/ResultsPage';
import ObjectivesPage from '@/pages/ObjectivesPage';
import BusinessEventsPage from '@/pages/BusinessEventsPage';
import InitiativesPage from '@/pages/InitiativesPage';
import ScenarioSimulatorPage from '@/pages/ScenarioSimulatorPage';
import RetentionPreviewPage from '@/pages/RetentionPreviewPage';
import RecommendationApprovalsPage from '@/pages/RecommendationApprovalsPage';
import SentinelsPage from '@/pages/SentinelsPage';
import SentinelBuilderPage from '@/pages/SentinelBuilderPage';
import SentinelDetailPage from '@/pages/SentinelDetailPage';
import IncidentsPage from '@/pages/IncidentsPage';
import MyFeedbackPage from '@/pages/MyFeedbackPage';
import DemoEnvironmentPage from '@/pages/DemoEnvironmentPage';
import AdministrationCategoryPage from '@/pages/AdministrationCategoryPage';
import PlatformConsoleHubPage from '@/pages/PlatformConsoleHubPage';
import AdminBrandingPage from '@/pages/AdminBrandingPage';
import AdminCoachSettingsPage from '@/pages/AdminCoachSettingsPage';
import AdminReleaseValidationPage from '@/pages/AdminReleaseValidationPage';
import AdminPilotConsolePage from '@/pages/AdminPilotConsolePage';
import AdminUsageBillingPage from '@/pages/AdminUsageBillingPage';
import AppsPage from '@/pages/AppsPage';
import AppDetailPage from '@/pages/AppDetailPage';
import CrossLineagePage from '@/pages/CrossLineagePage';
import MarketplacePage from '@/pages/MarketplacePage';
import CustomVisualsPage from '@/pages/CustomVisualsPage';
import CustomConnectorsPage from '@/pages/CustomConnectorsPage';
import SsoSettingsPage from '@/pages/SsoSettingsPage';
import FeedbackInboxPage from '@/pages/FeedbackInboxPage';
import ExportAuditPage from '@/pages/ExportAuditPage';
import UserActivityPage from '@/pages/UserActivityPage';
import DocHealthPage from '@/pages/DocHealthPage';
import PyramidMappingConsolePage from '@/pages/PyramidMappingConsolePage';
import CommunityModerationPage from '@/pages/CommunityModerationPage';
import GovernanceCenterPage from '@/pages/GovernanceCenterPage';
import AdoptionAnalyticsPage from '@/pages/AdoptionAnalyticsPage';
import RequestDemoPage from '@/pages/RequestDemoPage';
import BrandedLoginPage from '@/pages/BrandedLoginPage';
import CoachStudioPage from '@/pages/CoachStudioPage';
import AdminCoachAutonomyPage from '@/pages/AdminCoachAutonomyPage';import { trackPageView } from '@/lib/telemetry';

// Gate for the "/welcome" marketing landing page. When the deployment is
// configured for a login-first experience (root_page === 'login'), the
// landing page is fully hidden and visitors are sent straight to sign-in.
function LandingGate() {
  const { appConfig, appConfigLoaded } = useAuth();
  if (!appConfigLoaded) return null;
  if (appConfig?.root_page === 'login') return <Navigate to="/login" replace />;
  return <LandingPage />;
}

function ProtectedRoute({ children }) {
  const { user, loading, appConfig, appConfigLoaded } = useAuth();
  const location = useLocation();

  if (loading || (!user && location.pathname === '/' && !appConfigLoaded)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center animate-pulse">
            <svg className="w-5 h-5 text-primary animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Unauthenticated visitors to the site root see the marketing landing
    // page — UNLESS the deployment is configured to go straight to login
    // (root_page === 'login'), used for white-labelled customer installs.
    // Deep links still send them through login → return so they end up
    // where they intended.
    if (location.pathname === '/' && !location.search && !location.hash) {
      return <Navigate to={appConfig?.root_page === 'login' ? '/login' : '/welcome'} replace />;
    }
    const next = encodeURIComponent(location.pathname + location.search + location.hash);
    return <Navigate to={`/login?next=${next}`} state={{ from: location }} replace />;
  }

  // New / admin-reset accounts must set a fresh password before using the app.
  if (user.must_change_password) {
    return <ForcePasswordChange />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  // Fire a page_view telemetry event on every route change (guarded so
  // it never runs on auth/embed pages where there's no session).
  useEffect(() => {
    if (location.pathname && !location.pathname.startsWith('/login') && !location.pathname.startsWith('/register') && !location.pathname.startsWith('/embed') && !location.pathname.startsWith('/snapshot') && !location.pathname.startsWith('/welcome') && !location.pathname.startsWith('/request-demo')) {
      trackPageView(location.pathname);
    }
  }, [location.pathname]);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/welcome" element={<LandingGate />} />
      <Route path="/demo/autonomous-analytics-conference" element={<ConferenceDemoPage />} />
      <Route path="/request-demo" element={<RequestDemoPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/embed/:type/:id" element={<EmbedPage />} />
      <Route path="/embed-playground" element={<EmbedPlaygroundPage />} />
      <Route path="/developer-docs" element={<PublicDocsPage />} />
      <Route
        path="/embed/v2/bootstrap"
        element={
          <EmbedTokenProvider>
            <EmbedBootstrapPage />
          </EmbedTokenProvider>
        }
      />
      <Route
        path="/embed/v2/insights"
        element={
          <EmbedTokenProvider>
            <EmbedInsightsPage />
          </EmbedTokenProvider>
        }
      />
      <Route
        path="/embed/v2/kpi-monitor"
        element={
          <EmbedTokenProvider>
            <EmbedKpiMonitorPage />
          </EmbedTokenProvider>
        }
      />
      <Route
        path="/embed/v2/view/:resource/:id"
        element={
          <EmbedTokenProvider>
            <EmbedContentViewerPage />
          </EmbedTokenProvider>
        }
      />
      <Route
        path="/embed/v2/activity"
        element={
          <EmbedTokenProvider>
            <EmbedActivityFeedPage />
          </EmbedTokenProvider>
        }
      />
      <Route
        path="/embed/v2/explore"
        element={
          <EmbedTokenProvider>
            <EmbedExplorePage />
          </EmbedTokenProvider>
        }
      />
      <Route
        path="/embed/v2/investigate"
        element={
          <EmbedTokenProvider>
            <EmbedInvestigatePage />
          </EmbedTokenProvider>
        }
      />
      <Route path="/investigations/shared/:shareId" element={<PublicInvestigationPage />} />
      <Route
        path="/embed/v2/marketplace"
        element={
          <EmbedTokenProvider>
            <EmbedMarketplacePage />
          </EmbedTokenProvider>
        }
      />
      <Route
        path="/embed/v2/er-diagram"
        element={
          <EmbedTokenProvider>
            <EmbedErDiagramPage />
          </EmbedTokenProvider>
        }
      />
      <Route
        path="/embed/v2/admin/:panel"
        element={
          <EmbedTokenProvider>
            <EmbedAdminPanelPage />
          </EmbedTokenProvider>
        }
      />
      <Route path="/snapshot/investigation/:token" element={<InvestigationSnapshotPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="quickstart" element={<QuickstartPage />} />
        <Route path="dashboards" element={<ReportsPage />} />
        <Route path="reports/compare" element={<ReportComparePage />} />
        <Route path="change-requests" element={<ChangeListsPage />} />
        <Route path="dashboards/view" element={<ReportViewPage />} />
        <Route path="dashboards/builder" element={<ReportTabsPage />} />
        {/* Iter D — new dashboard authoring surface (tabs, cross-filter,
            click behaviours, small multiples, snap-to-grid, card compare). */}
        <Route path="dashboards/create" element={<DashboardBuilderPage />} />
        <Route path="dashboards/create/:dashId" element={<DashboardBuilderPage />} />
        <Route path="reports/view" element={<ReportViewPage />} />
        <Route path="reports/builder" element={<ReportTabsPage />} />
        <Route path="data-sources" element={<DataSourcesPage />} />
        <Route path="semantic-models" element={<SemanticModelsPage />} />
        <Route path="semantic-models/compare" element={<ModelComparePage />} />
        <Route path="semantic-models/:modelId/aggregates" element={<AggregateGovernancePage />} />
        <Route path="ai-usage" element={<AIUsagePage />} />
        <Route path="ai-copilot" element={<AICopilotPage />} />
        <Route path="ai-coverage" element={<AiCoveragePage />} />
        <Route path="recommendations" element={<RecommendationsHubPage />} />
        <Route path="my-work" element={<MyWorkPage />} />
        <Route path="my-work/recommendation/:recId" element={<RecommendationDetailPage />} />
        <Route path="decision-policies" element={<DecisionPoliciesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="results" element={<ResultsPage />} />
        <Route path="objectives" element={<ObjectivesPage />} />
        <Route path="business-events" element={<BusinessEventsPage />} />
        <Route path="initiatives" element={<InitiativesPage />} />
        <Route path="glossary" element={<GlossaryPage />} />
        <Route path="discussions" element={<DiscussionsPage threadsOnly />} />
        <Route path="threads" element={<MessagesPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="activity" element={<ActivityPage />} />
        <Route path="scheduling" element={<SchedulingPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="access-control" element={<AccessControlPage />} />
        <Route path="admin/pii-compliance" element={<PiiCompliancePage />} />
        <Route path="data-prep" element={<DataPrepPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="admin/usage-billing" element={<AdminUsageBillingPage />} />
        <Route path="admin/jobs" element={<AdminJobsPage />} />
        <Route path="admin/demo-env" element={<DemoEnvironmentPage />} />
        <Route path="admin/inbound-emails" element={<InboundEmailsPage />} />
        <Route path="admin/trial-leads" element={<ShareLeadsPage />} />
        <Route path="admin/signup-invites" element={<SignupInvitesPage />} />
        <Route path="admin/signup-requests" element={<SignupRequestsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="billing/success" element={<BillingPage />} />
        <Route path="visual-library" element={<VisualLibraryPage />} />
        <Route path="visual-library/edit" element={<VisualEditorPage />} />
        <Route path="visual-library/preview" element={<VisualEditorPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="tenant-admin" element={<TenantAdminPage />} />
        <Route path="kpi-monitor" element={<KpiMonitorPage />} />
        <Route path="system-logs" element={<LoggingPage />} />
        <Route path="lineage" element={<LineagePage />} />
        <Route path="embed-manager" element={<EmbedManagerPage />} />
        <Route path="anomaly-scans" element={<AnomalyScanPage />} />
        <Route path="forecast-sentinels" element={<ForecastSentinelsPage />} />
        <Route path="admin/deploy-status" element={<DeployStatusPage />} />
        <Route path="admin/status" element={<StatusPage />} />
        <Route path="apps" element={<AppsPage />} />
        <Route path="apps/:id" element={<AppDetailPage />} />
        <Route path="lineage-explorer" element={<CrossLineagePage />} />
        <Route path="marketplace" element={<MarketplacePage />} />
        <Route path="sdk/visuals" element={<CustomVisualsPage />} />
        <Route path="sdk/connectors" element={<CustomConnectorsPage />} />
        <Route path="admin/sso" element={<SsoSettingsPage />} />
        <Route path="admin/feedback" element={<FeedbackInboxPage />} />
        <Route path="admin/export-logs" element={<ExportAuditPage />} />
        <Route path="admin/user-activity" element={<UserActivityPage />} />
        <Route path="admin/doc-health" element={<DocHealthPage />} />
        <Route path="admin/pyramid-mappings" element={<PyramidMappingConsolePage />} />
        <Route path="admin/branding" element={<AdminBrandingPage />} />
        <Route path="admin/coach" element={<AdminCoachSettingsPage />} />
        <Route path="admin/coach-release" element={<AdminReleaseValidationPage />} />
        <Route path="admin/coach-pilot" element={<AdminPilotConsolePage />} />
        <Route path="admin/coach-autonomy" element={<AdminCoachAutonomyPage />} />
        <Route path="governance" element={<GovernanceCenterPage />} />
        <Route path="admin/community" element={<CommunityModerationPage />} />
        <Route path="admin/explore-eval" element={<ExploreEvalPage />} />
        <Route path="admin/all-schedules" element={<AllSchedulesPage />} />
        <Route path="admin/tenant-schedules" element={<TenantSchedulesPage />} />
        <Route path="admin/tenants" element={<AdminTenantsPage />} />
        <Route path="admin/revenue" element={<RevenueDashboardPage />} />
        <Route path="ai-insights" element={<InsightsFeedPage />} />
        <Route path="admin/llm" element={<LLMSettingsPage />} />
        <Route path="admin/storage" element={<StorageSettingsPage />} />
        <Route path="admin/system-updates" element={<SystemUpdatesPage />} />
        <Route path="admin/git-backup" element={<GitBackupPage />} />
        <Route path="admin/voice" element={<VoiceSettingsPage />} />
        <Route path="admin/ai-agents" element={<AiAgentsPage />} />
        <Route path="admin/ai-traces" element={<AiTraceLogPage />} />
        <Route path="admin/investigations-analytics" element={<InvestigationAnalyticsPage />} />
        <Route path="admin/certification-queue" element={<CertificationQueuePage />} />
        <Route path="investigations" element={<InvestigationsHistoryPage />} />
        <Route path="executive-summaries" element={<ExecutiveSummariesPage />} />
        <Route path="morning-brief" element={<MorningBriefPage />} />
        <Route path="investigations/diff" element={<InvestigationDiffPage />} />
        <Route path="developers" element={<DevelopersPage />} />
        <Route path="platform-admin" element={<PlatformAdminPage />} />
        <Route path="admin/version-control" element={<VersionControlPage />} />
        <Route path="admin/site-root" element={<PlatformSiteRootPage />} />
        <Route path="admin/feature-flags" element={<FeatureFlagsPage />} />
        <Route path="admin/platform-billing" element={<PlatformBillingRollupPage />} />
        <Route path="administration" element={<AdministrationHubPage />} />
        <Route path="coach-studio" element={<CoachStudioPage />} />
        <Route path="admin/metric-registry" element={<MetricRegistryPage />} />
        <Route path="admin/catalog" element={<DataCatalogPage />} />
        <Route path="admin/calculation-packs" element={<CalculationPacksPage />} />
        <Route path="admin/data-quality" element={<DataQualityPage />} />
        <Route path="admin/explore-kpis" element={<ExploreKpisPage />} />
        <Route path="admin/adoption-analytics" element={<AdoptionAnalyticsPage />} />
        <Route path="admin/predictive-models" element={<PredictiveModelsPage />} />
        <Route path="admin/risk-signals" element={<RiskSignalsPage />} />
        <Route path="admin/drift-alerts" element={<DriftAlertCenterPage />} />
        <Route path="admin/sentinel-packs" element={<PackLibraryPage />} />
        <Route path="admin/recommendations" element={<RecommendationsPage />} />
        <Route path="admin/scenario-simulator" element={<ScenarioSimulatorPage />} />
        <Route path="admin/what-if-analysis" element={<ScenarioSimulatorPage />} />
        <Route path="admin/retention-preview" element={<RetentionPreviewPage />} />
        <Route path="admin/recommendation-approvals" element={<RecommendationApprovalsPage />} />
        <Route path="admin/sentinels" element={<SentinelsPage />} />
        <Route path="admin/sentinels/new" element={<SentinelBuilderPage />} />
        <Route path="admin/sentinels/:sentinelId/edit" element={<SentinelBuilderPage />} />
        <Route path="admin/sentinels/:sentinelId" element={<SentinelDetailPage />} />
        <Route path="admin/incidents" element={<IncidentsPage />} />
        <Route path="my-feedback" element={<MyFeedbackPage />} />
        <Route path="administration/:categoryId" element={<AdministrationCategoryPage />} />
        <Route path="platform-console" element={<PlatformConsoleHubPage />} />
        <Route path="platform-console/:categoryId" element={<AdministrationCategoryPage />} />
      </Route>
      {/* Branded-login slug (e.g. /cloverleaf) — sits BELOW all static
          routes so they always win first; unknown slug falls back to
          /login inside the page. */}
      <Route path="/:slug" element={<BrandedLoginPage />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <ReportTabsProvider>
            <WorkspaceProvider>
              <GlobalShortcutsProvider>
                <CollabProvider>
                  <AppRouter />
                  <KeyboardShortcutsDialog />
                  <CommandPalette />
                </CollabProvider>
              </GlobalShortcutsProvider>
            </WorkspaceProvider>
          </ReportTabsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
